import { getBasicEntityFromNominee } from '../hooks/useNomineeEntity';
import type {
  AssetPlayer,
  AssetTeam,
  CardTracking,
  NomineeTrackingParams,
  NomineeType,
  PollType,
  SponsorType,
} from '../types';
import { PollStatus } from '../types';

import GoogleTagManagerHelper from './googleTagManager';
import { getAppCode, getVideoArticleAsset } from './utils';

const eventHandlers = {
  click: (trackingOptions: CardTracking[]) => GoogleTagManagerHelper.gtmCardClick(trackingOptions),
  impression: (trackingOptions: CardTracking[]) => GoogleTagManagerHelper.gtmCardImpression(trackingOptions),
};

const getNomineeInfo = (nominee: NomineeType, appType: string) => {
  const nomineeEntity = getBasicEntityFromNominee(nominee, appType);
  const nomineeInfo = nominee.assets?.find((a) => a.type.toLowerCase() === nomineeEntity.type)?.data as
    | AssetTeam
    | AssetPlayer;
  return nomineeInfo;
};

const buildNomineeTrackingOptions = (
  poll: PollType,
  nominee: NomineeType,
  appType: string,
  competition: string,
  sponsor: SponsorType,
  extraOptions: Partial<CardTracking> = {},
) => {
  const index = poll?.nominees.findIndex((n) => n.id === nominee.id);
  const nomineeInfo = getNomineeInfo(nominee, appType);
  const pollCode = getAppCode(appType, poll.subtype).toUpperCase();
  const hasVideo = !!getVideoArticleAsset(nominee);
  const pollTitle = poll?.translations?.title ? ` - ${poll?.translations?.title?.EN}` : '';
  const defaultOptions: CardTracking = {
    item_name: `${pollCode} - ${nomineeInfo.internationalName}`,
    item_id: nomineeInfo.id,
    item_brand: competition,
    item_category: `${poll?.type?.toLowerCase()} poll`,
    item_category2: hasVideo ? 'video' : 'image',
    ...(sponsor ? { item_category3: 'sponsored', item_category4: sponsor.code } : {}),
    item_category5: extraOptions?.item_category5 ?? poll?.status?.toLocaleLowerCase(),
    item_list_name: `${poll?.translations?.typeDescription?.EN ?? appType.toUpperCase()}${pollTitle}`,
    item_list_id: poll?.id,
    index,
  };

  return [{ ...defaultOptions, ...extraOptions }];
};

const buildListNameFromHTML = (el: HTMLElement) => {
  let containerPosition: number;
  let container: string;
  const parent = el.closest<HTMLElement>('.js-tracking-container');
  if (parent) {
    if (!parent.dataset.position) {
      parent.dataset.position = [...document.querySelectorAll('.js-tracking-container')]
        .findIndex((el) => el === parent)
        .toString();
    }

    container = parent.dataset.trackingContainer;
    containerPosition = +parent.dataset.position + 1;
  }

  const list = [
    GoogleTagManagerHelper.getFromDataLayer('pageType'),
    GoogleTagManagerHelper.getFromDataLayer('pageSubType'),
    container,
    containerPosition,
  ]
    .filter(Boolean)
    .join('-');

  return list;
};

const trackNomineeEvent = (
  type: 'click' | 'impression',
  options: NomineeTrackingParams & { extraOptions?: Partial<CardTracking> },
) => {
  const { poll, nominee, appType, competition, sponsor, extraOptions } = options;
  const trackingOptions = buildNomineeTrackingOptions(poll, nominee, appType, competition, sponsor, extraOptions);
  eventHandlers[type](trackingOptions);
};

export const trackSectionNomineeImpression = (params: NomineeTrackingParams) => {
  trackNomineeEvent('impression', {
    ...params,
    extraOptions: params.poll?.status === PollStatus.OPEN && params.userVoted ? { item_category5: 'voted' } : {},
  });
};

export const trackSectionNomineeVoteClick = (params: NomineeTrackingParams) => {
  trackNomineeEvent('click', {
    ...params,
    extraOptions: { item_variant: `vote - ${getNomineeInfo(params.nominee, params.appType).internationalName}` },
  });
};

export const trackNomineeDetailLinkClick = (params: NomineeTrackingParams) => {
  trackNomineeEvent('click', {
    ...params,
    extraOptions: { item_variant: 'link' },
  });
};

export const trackWidgetNomineeVoteClick = (params: NomineeTrackingParams) => {
  const nomineeInfo = getNomineeInfo(params.nominee, params.appType);

  trackNomineeEvent('click', {
    ...params,
    extraOptions: {
      item_name: `${getAppCode(params.appType, params.poll.subtype).toUpperCase()} - ${params.poll?.translations?.title?.EN}`,
      item_category: `${params.poll?.type?.toLowerCase()} poll widget`,
      item_id: params.poll?.id,
      item_list_name: buildListNameFromHTML(params.htmlEl),
      item_variant: `vote - ${nomineeInfo.internationalName}`,
    },
  });
};
